import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Field, Modal, ModalActions, NotProvided } from '@evogard/ui';
import {
  cents,
  technologyFeeTiersRefusal,
  toColumn,
  type TechnologyFeeTier,
} from '@evogard/domain';
import type { TechnologyFeeTierOut } from '@evogard/contract';
import { api, errorMessage, ok } from '../../lib/api.js';

const KEY = ['management', 'technology-fee-tiers'] as const;

/**
 * As faixas da taxa de tecnologia da praça (05/10/2026) — a partir de quanto
 * o lote paga quanto. Quem lê é quem vê o quadro; quem salva tem
 * `management.technology_fee:editar`. A regra de uma tabela válida é a mesma
 * da API (`technologyFeeTiersRefusal`), conferida antes de mandar.
 */
export function TechnologyFeeModal({
  canEdit,
  onClose,
}: {
  canEdit: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: KEY,
    queryFn: async () =>
      ok(
        await api.management.technologyFeeTiers(),
        200,
        'Não foi possível ler as faixas da taxa de tecnologia.',
      ),
  });

  return (
    <Modal
      open
      onOpenChange={(open) => !open && onClose()}
      title="Taxa de tecnologia"
      descricao="Descontada da premiação do consultor externo, num lote do mês: por padrão o maior, ou o que o cadastro do consultor fixar. A faixa sai do valor desse lote, e vale a partir do valor escrito — R$ 500,00 exatos já pagam a faixa de 500. Mudar aqui vale para os meses cuja primeira nota ainda não foi enviada."
      size="md"
    >
      {query.isError && <Alert tone="erro">{query.error.message}</Alert>}
      {query.data && (
        <TiersForm
          initial={query.data}
          canEdit={canEdit}
          onSaved={(saved) => {
            queryClient.setQueryData(KEY, saved);
            onClose();
          }}
          onCancel={onClose}
        />
      )}
    </Modal>
  );
}

interface Row {
  from: string;
  fee: string;
}

function TiersForm({
  initial,
  canEdit,
  onSaved,
  onCancel,
}: {
  initial: TechnologyFeeTierOut[];
  canEdit: boolean;
  onSaved: (saved: TechnologyFeeTierOut[]) => void;
  onCancel: () => void;
}) {
  const [rows, setRows] = useState<Row[]>(
    initial.length > 0
      ? initial.map((t) => ({ from: asInput(t.fromAmount), fee: asInput(t.fee) }))
      : [{ from: '0,00', fee: '0,00' }],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (i: number, field: keyof Row, value: string) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

  const save = async () => {
    setError(null);
    const parsed = rows.map((r) => ({ from: centsOf(r.from), fee: centsOf(r.fee) }));
    if (parsed.some((p) => p.from === null || p.fee === null)) {
      setError('Preencha cada faixa com valores em reais, como 1.000,00.');
      return;
    }
    const tiers: TechnologyFeeTier[] = parsed.map((p) => ({
      from: cents(p.from!),
      fee: cents(p.fee!),
    }));
    const refusal = technologyFeeTiersRefusal(tiers);
    if (refusal !== null) {
      setError(refusal);
      return;
    }
    setBusy(true);
    try {
      const r = await api.management.saveTechnologyFeeTiers({
        body: {
          tiers: tiers.map((t) => ({ fromAmount: toColumn(t.from), fee: toColumn(t.fee) })),
        },
      });
      if (r.status !== 200) throw new Error(errorMessage(r.body));
      onSaved(r.body);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Algo deu errado.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canEdit) void save();
      }}
    >
      {initial.length === 0 && (
        <Alert tone="alerta" title="Nenhuma faixa cadastrada nesta base.">
          Enquanto não houver faixa, nenhum consultor externo consegue enviar a nota: o valor
          dela depende do desconto.
        </Alert>
      )}
      {rows.map((r, i) => (
        <fieldset key={i} className="grid grid-cols-[1fr_1fr_auto] items-end gap-3">
          <legend className="sr-only">Faixa {i + 1}</legend>
          <Field
            label="A partir de"
            inputMode="decimal"
            prefix="R$"
            disabled={!canEdit}
            value={r.from}
            onChange={(e) => set(i, 'from', e.target.value)}
          />
          <Field
            label="Taxa"
            inputMode="decimal"
            prefix="R$"
            disabled={!canEdit}
            value={r.fee}
            onChange={(e) => set(i, 'fee', e.target.value)}
          />
          {canEdit && (
            <Button
              variant="contorno"
              icon="lixeira"
              aria-label={`Remover a faixa ${i + 1}`}
              disabled={rows.length === 1}
              onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
            />
          )}
        </fieldset>
      ))}
      {canEdit && (
        <div>
          <Button
            variant="contorno"
            icon="mais"
            onClick={() => setRows((rs) => [...rs, { from: '', fee: '' }])}
          >
            Adicionar faixa
          </Button>
        </div>
      )}
      {!canEdit && (
        <NotProvided>
          Você pode ver as faixas, mas quem as altera tem outra permissão.
        </NotProvided>
      )}
      {error && <Alert tone="erro">{error}</Alert>}
      <ModalActions>
        <Button variant="contorno" onClick={onCancel}>
          {canEdit ? 'Cancelar' : 'Fechar'}
        </Button>
        {canEdit && (
          <Button type="submit" loading={busy}>
            Salvar
          </Button>
        )}
      </ModalActions>
    </form>
  );
}

/** "1500.00" do fio → "1500,00" no campo. */
function asInput(column: string): string {
  return column.replace('.', ',');
}

/** "1.234,56", "1234,56" e "1234.56" — em centavos; `null` quando não é número. */
function centsOf(text: string): number | null {
  let t = text.trim().replace(/\s/g, '');
  if (t === '') return null;
  if (t.includes(',')) t = t.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [whole = '0', decimal = ''] = t.split('.');
  return Number(whole) * 100 + Number(decimal.padEnd(2, '0'));
}
